import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { assert } from '@/utils'
import { queryKeys } from '@/query-keys'
import { assertIsDeliverTxSuccess, IBCTransferStatus, broadcastIbcTransaction2 } from '@/wallet-utils'
import { traceIbcStatus2 } from '@/wallet-utils/ibc/trace-ibc-status'
import { MutationParameters } from './types'
import { useSelectedDenom } from '../../../../contexts/denom'
import { useSelectedChain } from '@/contexts/wallet'

export type BroadcastSendTokenMutationMode = 'deposit' | 'withdraw'

export interface BroadcastSendTokenMutationReturnType {
  tx: DeliverTxResponse
  status: IBCTransferStatus
}

// Top-level things that this mutation does:
// - Fetches airdrop claimable amount
// - Broadcasts an ibc transfer
// - Display banner for claimed amount
const useBroadcastStakeAutopilotMutation = ({
  stakeAutopilotRaw,
  setStakeAutopilotTransaction
}: MutationParameters) => {
  const queryClient = useQueryClient()

  const { getSigningStargateClient, getAccount, getOfflineSigner, wallet } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const handleMutation = async (): Promise<BroadcastSendTokenMutationReturnType> => {
    assert(stakeAutopilotRaw, 'Unable to broadcast transaction without a signed transaction raw.')

    assert(wallet, `You are unable to send token without your ${denom} wallet instance.`)

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = { client, denom, account, offlineSigner: getOfflineSigner(), wallet: wallet.name }
    const tx = await broadcastIbcTransaction2(ibcParams, stakeAutopilotRaw)

    assertIsDeliverTxSuccess(tx)

    const status = await traceIbcStatus2({ denom, tx })

    if (status === 'return-later') {
      return { tx, status: 'return-later' }
    }

    if (status === 'timeout') {
      return { tx, status: 'timeout' }
    }

    return { tx, status: 'complete' }
  }

  const handleSettled = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase })
  }

  const handleSuccess = async ({ tx }: BroadcastSendTokenMutationReturnType) => {
    setStakeAutopilotTransaction(tx)

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.transactionHistoryIbcStatusByHash({ hash: tx.transactionHash })
      }),
      queryClient.invalidateQueries({ queryKey: queryKeys.chainBalancesBase })
    ])
  }

  return useMutation({
    mutationFn: handleMutation,
    onSettled: handleSettled,
    onSuccess: handleSuccess
  })
}

export { useBroadcastStakeAutopilotMutation }
